/**
 * Execution Notifications Hook
 * Handles display of execution-related notifications
 * Decouples polling logic from UI notifications (SRP)
 */

import { useCallback } from 'react';
import { useUIStore } from '@flow-studio/store';
import { useSettingsStore } from '@flow-studio/store';
import type { ExecutionState } from '@flow-studio/types';

interface UseExecutionNotificationsOptions {
  enableNotifications?: boolean;
}

/**
 * Hook to manage execution notifications
 * Provides callbacks for execution events that display toast messages
 */
export const useExecutionNotifications = (
  options?: UseExecutionNotificationsOptions
) => {
  const showToast = useUIStore((s) => s.showToast);
  const settings = useSettingsStore((s) => s.settings);

  const shouldShowNotifications =
    options?.enableNotifications !== false &&
    settings.showExecutionNotifications;

  /**
   * Notify when execution starts
   * Accepts ResID (GUID string from ExecutionID)
   */
  const notifyExecutionStarted = useCallback(
    (ResID: string) => {
      if (shouldShowNotifications) {
        // Display first 8 chars of GUID for readability
        const displayId = ResID.substring(0, 8) + '...';
        showToast('info', `Execution started. ID: ${displayId}`);
      }
    },
    [shouldShowNotifications, showToast]
  );

  /**
   * Notify when execution completes
   */
  const notifyExecutionCompleted = useCallback(
    (finalState: ExecutionState) => {
      if (!shouldShowNotifications) return;

      const messageMap: Record<ExecutionState, string> = {
        completed: 'Workflow execution completed successfully',
        completedWithWarnings: 'Workflow completed with warnings',
        failed: 'Workflow execution failed',
        cancelled: 'Workflow execution cancelled',
        timedOut: 'Workflow execution timed out',
        idle: '',
        queued: '',
        running: '',
        paused: '',
        waiting: '',
      };

      const message = messageMap[finalState];
      if (message) {
        const toastType =
          finalState === 'completed'
            ? 'success'
            : finalState === 'completedWithWarnings'
              ? 'warning'
              : 'error';
        showToast(toastType, message);
      }
    },
    [shouldShowNotifications, showToast]
  );

  /**
   * Notify when execution has an error
   */
  const notifyExecutionError = useCallback(
    (error: string, context?: string) => {
      if (shouldShowNotifications) {
        const message = context ? `${context}: ${error}` : error;
        showToast('error', message);
      }
    },
    [shouldShowNotifications, showToast]
  );

  /**
   * Notify when polling connection is lost
   */
  const notifyConnectionLost = useCallback(() => {
    if (shouldShowNotifications) {
      showToast('warning', 'Lost connection to execution server');
    }
  }, [shouldShowNotifications, showToast]);

  /**
   * Notify when execution is paused
   */
  const notifyExecutionPaused = useCallback(() => {
    if (shouldShowNotifications) {
      showToast('info', 'Execution paused');
    }
  }, [shouldShowNotifications, showToast]);

  /**
   * Notify when execution is resumed
   */
  const notifyExecutionResumed = useCallback(() => {
    if (shouldShowNotifications) {
      showToast('info', 'Execution resumed');
    }
  }, [shouldShowNotifications, showToast]);

  /**
   * Notify when execution is cancelled
   */
  const notifyExecutionCancelled = useCallback(() => {
    if (shouldShowNotifications) {
      showToast('warning', 'Execution cancelled');
    }
  }, [shouldShowNotifications, showToast]);

  return {
    notifyExecutionStarted,
    notifyExecutionCompleted,
    notifyExecutionError,
    notifyConnectionLost,
    notifyExecutionPaused,
    notifyExecutionResumed,
    notifyExecutionCancelled,
  };
};
